import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Disableable, getTextPresetCss, getTextTokenPreset } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { BORDER_RADIUS, SPACING, TEXT_PRESET_KEY } from '@optimus-web/local-tokens';
import { Icon, IconProps } from './Icon';
import { getDefaultTextSecondaryColor, getHorizontalInputPadding, getSmallHorizontalInputPadding } from './theme';
import { SizeType } from './types';

interface Clickable {
    onClick?: (e: React.MouseEvent) => void;
    role?: string;
}

export type ClickableIconProps = Pick<IconProps, Exclude<keyof IconProps, 'value'>> & Clickable;
export const ArrowIconElement = styled(
    forwardRef<HTMLSpanElement, ClickableIconProps>((props, ref) => (
        <Icon value={IconName.ChevronDown} ref={ref} {...props} />
    ))
)`
    cursor: pointer;
    outline: none;
`;

export const CalendarIconElement = styled(
    forwardRef<HTMLSpanElement, ClickableIconProps>((props, ref) => (
        <Icon value={IconName.Calendar} ref={ref} {...props} />
    ))
)`
    cursor: pointer;
    outline: none;
`;

interface TimeIconElementProps extends ClickableIconProps {
    opened?: boolean;
}

export const TimeIconElement = styled(
    forwardRef<HTMLSpanElement, TimeIconElementProps>((props, ref) => (
        <Icon value={IconName.Time} ref={ref} variant={props.opened ? 'primary' : 'basic'} {...props} />
    ))
)`
    cursor: pointer;
    outline: none;
`;

export interface PlaceholderElementProps extends Disableable {
    children?: React.ReactNode;
    smallLeftSpacing?: boolean;
    smallRightSpacing?: boolean;
    inputSize?: SizeType;
}

export const PlaceholderElement = styled.div<PlaceholderElementProps>`
    ${(props) => getTextPresetCss(TEXT_PRESET_KEY[props.inputSize === 'small' ? '200r' : '300r'])}
    color: ${getDefaultTextSecondaryColor};
    position: absolute;
    pointer-events: none;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    box-sizing: border-box;
    padding-left: ${(props) =>
        props.smallLeftSpacing || props.inputSize === 'small'
            ? getSmallHorizontalInputPadding(props)
            : getHorizontalInputPadding(props)}px;
    padding-right: ${(props) =>
        props.smallRightSpacing || props.inputSize === 'small'
            ? getSmallHorizontalInputPadding(props)
            : getHorizontalInputPadding(props)}px;
`;

export const DetailContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
`;

export const RightDetailContainer = styled(DetailContainer)`
    border-top-right-radius: ${BORDER_RADIUS[50]};
    border-bottom-right-radius: ${BORDER_RADIUS[50]};
    padding-right: ${SPACING[100]}px;
`;

export const LeftDetailContainer = styled(DetailContainer)`
    color: ${getDefaultTextSecondaryColor};
    border-top-left-radius: ${BORDER_RADIUS[50]};
    border-bottom-left-radius: ${BORDER_RADIUS[50]};
    padding: 0 ${SPACING[100]}px;
`;

export const RestTagCounterContainer = styled(DetailContainer)`
    padding-right: ${SPACING[100]}px;
`;

export const REQUIRED_MARKER = css`
    &::after {
        content: '*';
        display: inline;
    }
`;

interface FormLabelProps {
    required?: boolean;
}

export const FormLabel = styled.label<FormLabelProps>`
    ${getTextTokenPreset('BODY_M')};
    padding: 0;
    ${({ required }) => required && REQUIRED_MARKER}
`;

interface GetInputTextPresetProps {
    inputSize?: SizeType;
    size?: SizeType;
}

export const getInputTextPresetCss = ({ inputSize, size }: GetInputTextPresetProps) =>
    getTextPresetCss(TEXT_PRESET_KEY[inputSize === 'small' || size === 'small' ? '200r' : '300r']);
